import React, { useEffect, useRef, useState, useId, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Keyboard } from '@capacitor/keyboard';
import { modalStack, MODAL_TIMINGS } from './modalStack';

export interface KlSheetProps {
    open: boolean;
    onClose: () => void;
    title?: React.ReactNode;
    subtitle?: React.ReactNode;
    height?: 'auto' | 'fixed' | 'fraction';
    fixedHeight?: number | string; // Ej: 372, 420, 560, '64%'
    top?: number | string; // Para hojas altas que arrancan a una distancia fija de arriba, ej: 40, 56
    footer?: React.ReactNode;
    origin?: { x: number; y: number } | null;
    blurBackdrop?: boolean; // true por defecto
    children: React.ReactNode;
    id?: string;
    showCloseButton?: boolean;
    dragHandle?: boolean;
    contentClassName?: string;
}

export const KlSheet: React.FC<KlSheetProps> = ({
    open,
    onClose,
    title,
    subtitle,
    height = 'auto',
    fixedHeight,
    top,
    footer,
    origin,
    blurBackdrop = true,
    children,
    id: propId,
    showCloseButton = true,
    dragHandle = true,
    contentClassName = ''
}) => {
    const autoId = useId();
    const sheetId = propId || `kl-sheet-${autoId}`;

    const [isMounted, setIsMounted] = useState(false);
    const [animationState, setAnimationState] = useState<'closed' | 'opening' | 'open' | 'closing'>('closed');
    const [dragY, setDragY] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [stackDepth, setStackDepth] = useState(0);
    const [computedHeightPx, setComputedHeightPx] = useState<number | null>(null);

    const sheetRef = useRef<HTMLDivElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);
    const dragStartYRef = useRef(0);
    const dragStartTimeRef = useRef(0);
    const lastDragYRef = useRef(0);
    const prevActiveElementRef = useRef<HTMLElement | null>(null);
    const didUnmountRef = useRef(false);
    const lastViewportHeightRef = useRef<number>(0);
    const keyboardHeightRef = useRef<number>(0);
    const isKeyboardOpenRef = useRef<boolean>(false);

    // Cálculo blindado de altura en px (se calcula UNA VEZ al abrir y se actualiza solo ante eventos de teclado)
    const calculateSheetHeight = useCallback((forcedKbHeight?: number) => {
        if (typeof window === 'undefined') return;
        const totalHeight = window.innerHeight;
        const vh = window.visualViewport ? window.visualViewport.height : totalHeight;

        let kbHeight = 0;
        if (forcedKbHeight !== undefined) {
            kbHeight = forcedKbHeight;
        } else if (isKeyboardOpenRef.current) {
            kbHeight = keyboardHeightRef.current;
        } else if (window.visualViewport) {
            const diff = totalHeight - window.visualViewport.height;
            if (diff > 120) {
                kbHeight = diff;
            }
        }

        let targetH = 0;
        if (top !== undefined) {
            const topPx = typeof top === 'number' ? top : (parseInt(top as string, 10) || 40);
            targetH = Math.max(200, totalHeight - topPx);
        } else if (height === 'fixed' && fixedHeight !== undefined) {
            const hVal = typeof fixedHeight === 'number' ? fixedHeight : (parseInt(fixedHeight as string, 10) || 400);
            targetH = Math.min(hVal, totalHeight - 40);
        } else if (height === 'fraction') {
            const frac = typeof fixedHeight === 'string' && fixedHeight.endsWith('%')
                ? (parseFloat(fixedHeight) / 100)
                : 0.8; // 80%
            const normalH = Math.round(totalHeight * frac);
            if (kbHeight > 0) {
                // Con teclado abierto, ocupa el espacio disponible sobre el teclado (mínimo 360px)
                const availableSpace = totalHeight - kbHeight - 16;
                targetH = Math.max(360, Math.min(normalH, availableSpace));
            } else {
                targetH = Math.min(normalH, totalHeight - 40);
            }
        } else {
            // Auto
            targetH = 0;
        }

        if (targetH > 0) {
            setComputedHeightPx(prev => {
                if (prev !== null && Math.abs(prev - targetH) < 2) return prev;
                return targetH;
            });
        }
    }, [top, height, fixedHeight]);

    const prefersReducedMotion = typeof window !== 'undefined'
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;

    const cleanupAndUnmount = useCallback(() => {
        if (didUnmountRef.current) return;
        didUnmountRef.current = true;
        setIsMounted(false);
        setAnimationState('closed');
        setDragY(0);
    }, []);

    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    // Registro en la pila modal y gestión de montado
    useEffect(() => {
        if (open) {
            // Si ya está montado y abierto, no reiniciar animación ni re-registrar
            if (isMounted && animationState === 'open') {
                return;
            }

            didUnmountRef.current = false;
            prevActiveElementRef.current = document.activeElement as HTMLElement | null;
            setIsMounted(true);

            // Registro en modalStack
            const unregister = modalStack.push({
                id: sheetId,
                type: 'sheet',
                onClose: () => onCloseRef.current(),
                returnFocusElement: prevActiveElementRef.current
            });

            setStackDepth(modalStack.getDepth(sheetId));

            // Iniciar animación de apertura (doble rAF para garantizar el estado inicial)
            setAnimationState('opening');
            const timer = requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setAnimationState('open');
                });
            });

            return () => {
                cancelAnimationFrame(timer);
                unregister();
            };
        } else if (isMounted) {
            // Animación de salida antes de desmontar
            setAnimationState('closing');

            // Respaldo de desmontaje seguro si transitionend no se dispara en duración + 50ms
            const durationMs = prefersReducedMotion
                ? MODAL_TIMINGS.reducedMotion.fadeMs
                : MODAL_TIMINGS.sheet.closeMs;

            const safetyTimer = setTimeout(() => {
                cleanupAndUnmount();
            }, durationMs + MODAL_TIMINGS.safetyMarginMs);

            return () => clearTimeout(safetyTimer);
        }
    }, [open, sheetId, prefersReducedMotion, cleanupAndUnmount]);

    // Actualizar profundidad si la pila cambia
    useEffect(() => {
        if (!isMounted) return;
        return modalStack.subscribe(() => {
            setStackDepth(modalStack.getDepth(sheetId));
        });
    }, [isMounted, sheetId]);

    // Control de altura estable (Punto 1b: sin oscilaciones al desbloquear o cambiar estado de app)
    useEffect(() => {
        if (!open || !isMounted) return;

        calculateSheetHeight();

        let kbShowHandle: any = null;
        let kbHideHandle: any = null;

        if (Capacitor.isPluginAvailable('Keyboard')) {
            Keyboard.addListener('keyboardWillShow', (info) => {
                isKeyboardOpenRef.current = true;
                keyboardHeightRef.current = info.keyboardHeight;
                calculateSheetHeight(info.keyboardHeight);
            }).then(h => { kbShowHandle = h; }).catch(() => {});

            Keyboard.addListener('keyboardWillHide', () => {
                isKeyboardOpenRef.current = false;
                keyboardHeightRef.current = 0;
                calculateSheetHeight(0);
            }).then(h => { kbHideHandle = h; }).catch(() => {});
        }

        // Si se usa visualViewport, procesar con rAF, delay 100ms e ignorar variaciones < 2px
        let resizeTimer: any = null;
        const handleViewportResize = () => {
            if (!window.visualViewport) return;
            const currentH = window.visualViewport.height;
            if (Math.abs(currentH - lastViewportHeightRef.current) < 2) return;

            if (resizeTimer) clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                requestAnimationFrame(() => {
                    const newH = window.visualViewport ? window.visualViewport.height : window.innerHeight;
                    if (Math.abs(newH - lastViewportHeightRef.current) >= 2) {
                        lastViewportHeightRef.current = newH;
                        calculateSheetHeight();
                    }
                });
            }, 100);
        };

        if (window.visualViewport) {
            lastViewportHeightRef.current = window.visualViewport.height;
            window.visualViewport.addEventListener('resize', handleViewportResize);
        }

        // Al volver a la app (appStateChange -> isActive o visibilitychange), recalcular UNA SOLA VEZ
        let appStateHandle: any = null;
        if (Capacitor.isPluginAvailable('App')) {
            CapacitorApp.addListener('appStateChange', (state) => {
                if (state.isActive) {
                    setTimeout(() => {
                        requestAnimationFrame(() => {
                            calculateSheetHeight();
                        });
                    }, 50);
                }
            }).then(h => { appStateHandle = h; }).catch(() => {});
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                setTimeout(() => {
                    requestAnimationFrame(() => {
                        calculateSheetHeight();
                    });
                }, 50);
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            if (kbShowHandle?.remove) kbShowHandle.remove();
            if (kbHideHandle?.remove) kbHideHandle.remove();
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', handleViewportResize);
            }
            if (resizeTimer) clearTimeout(resizeTimer);
            if (appStateHandle?.remove) appStateHandle.remove();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [open, isMounted, calculateSheetHeight]);

    // Foco atrapado y tecla Escape
    useEffect(() => {
        if (animationState !== 'open') return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && modalStack.isTop(sheetId)) {
                e.preventDefault();
                onClose();
                return;
            }

            if (e.key === 'Tab' && sheetRef.current) {
                const focusables = sheetRef.current.querySelectorAll<HTMLElement>(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                if (focusables.length === 0) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];

                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [animationState, sheetId, onClose]);

    // Gestión táctil de arrastre (Pointer Events)
    const handlePointerDown = useCallback((e: React.PointerEvent) => {
        // Solo botón principal
        if (e.button !== 0) return;

        // Si el toque proviene del cuerpo y no está arriba del todo, permitir scroll normal
        if (bodyRef.current && e.target !== e.currentTarget && bodyRef.current.contains(e.target as Node)) {
            if (bodyRef.current.scrollTop > 0) {
                return;
            }
        }

        dragStartYRef.current = e.clientY;
        dragStartTimeRef.current = performance.now();
        lastDragYRef.current = e.clientY;
        setIsDragging(true);

        const handlePointerMove = (moveEv: PointerEvent) => {
            const deltaY = moveEv.clientY - dragStartYRef.current;
            lastDragYRef.current = moveEv.clientY;

            if (deltaY > 0) {
                // Hacia abajo: 1 a 1
                setDragY(deltaY);
            } else {
                // Hacia arriba: resistencia 1/5
                setDragY(deltaY * 0.2);
            }
        };

        const handlePointerUp = () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
            setIsDragging(false);

            const duration = Math.max(1, performance.now() - dragStartTimeRef.current);
            const deltaY = lastDragYRef.current - dragStartYRef.current;
            const velocity = deltaY / duration; // px/ms

            if (deltaY > 80 || velocity > 0.5) {
                // Umbral superado: cerrar la hoja con inercia
                onClose();
            } else {
                // Regresar a su lugar con animación
                setDragY(0);
            }
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
    }, [onClose]);

    if (!isMounted) return null;

    // Cálculo de dimensiones de la hoja
    const sheetStyle: React.CSSProperties = prefersReducedMotion
        ? {
            transform: `translateY(${Math.max(0, dragY)}px)`,
            opacity: animationState === 'opening' || animationState === 'closing' ? 0 : 1,
            transition: isDragging
                ? 'none'
                : `opacity ${MODAL_TIMINGS.reducedMotion.fadeVar} ease`,
            willChange: isDragging || animationState === 'opening' || animationState === 'closing' ? 'opacity' : 'auto'
        }
        : {
            transform: animationState === 'opening' || animationState === 'closing'
                ? 'translateY(100%)'
                : `translateY(${Math.max(0, dragY)}px)`,
            opacity: 1,
            transition: isDragging
                ? 'none'
                : animationState === 'closing'
                    ? `transform ${MODAL_TIMINGS.sheet.closeVar} ${MODAL_TIMINGS.easeIn}`
                    : `transform ${MODAL_TIMINGS.sheet.openVar} ${MODAL_TIMINGS.easeOut}`,
            willChange: isDragging || animationState === 'opening' || animationState === 'closing' ? 'transform' : 'auto'
        };

    // Aplicar altura según modo (fija con bottom: 0 y altura en px calculada para evitar bucle de redimensionamiento)
    sheetStyle.bottom = 0;
    if (computedHeightPx !== null && computedHeightPx > 0) {
        sheetStyle.height = `${computedHeightPx}px`;
        sheetStyle.maxHeight = 'calc(100vh - 40px)';
    } else if (top !== undefined) {
        const topVal = typeof top === 'number' ? `${top}px` : top;
        sheetStyle.top = topVal;
        sheetStyle.height = `calc(100% - ${topVal})`;
    } else if (height === 'fixed' && fixedHeight !== undefined) {
        const hVal = typeof fixedHeight === 'number' ? `${fixedHeight}px` : fixedHeight;
        sheetStyle.height = hVal;
        sheetStyle.maxHeight = 'calc(100vh - 40px)';
    } else if (height === 'fraction') {
        sheetStyle.height = typeof fixedHeight === 'string' ? fixedHeight : '80%';
        sheetStyle.maxHeight = 'calc(100vh - 40px)';
    } else {
        // Auto
        sheetStyle.maxHeight = 'calc(100vh - 40px)';
    }

    // Si tiene origen explícito (ej: Asistente IA que nace desde el punto tocado)
    if (origin && animationState === 'opening') {
        sheetStyle.transformOrigin = `${origin.x}px ${origin.y}px`;
    }

    // Cálculo de opacidad del velo (se atenúa proporcionalmente al arrastrar hacia abajo)
    const scrimOpacity = Math.max(0, Math.min(1, 1 - dragY / 320));
    const isStacked = stackDepth > 0;
    const zIndex = 110 + stackDepth * 10;

    return createPortal(
        <div
            id={sheetId}
            data-kl-sheet="true"
            data-kl-modal-open={animationState === 'open' ? 'true' : 'false'}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? `${sheetId}-title` : undefined}
            className="fixed inset-0 flex flex-col justify-end"
            style={{
                zIndex,
                pointerEvents: animationState === 'closing' ? 'none' : 'auto'
            }}
        >
            {/* Velo de fondo (Scrim) */}
            <div
                className={`kl-modal-scrim ${animationState === 'open' ? 'is-open' : animationState === 'closing' ? 'is-closing' : ''} ${isStacked ? 'is-stacked' : ''}`}
                style={{
                    opacity: animationState === 'open' ? scrimOpacity : undefined,
                    backdropFilter: !blurBackdrop || isStacked ? 'none' : undefined,
                    WebkitBackdropFilter: !blurBackdrop || isStacked ? 'none' : undefined
                }}
                onClick={() => {
                    if (modalStack.isTop(sheetId)) {
                        onClose();
                    }
                }}
                aria-hidden="true"
            />

            {/* Contenedor de la Hoja Inferior (Mismos valores que maquetas: r=18px, shadow=0 -6px 18px rgba(0,0,0,.14)) */}
            <div
                ref={sheetRef}
                className="kl-sheet-container relative z-10 bg-white dark:bg-[#1a2634] rounded-t-[18px] flex flex-col overflow-hidden border-t border-slate-200 dark:border-slate-800"
                style={{
                    boxShadow: '0 -6px 18px rgba(0,0,0,.14)',
                    ...sheetStyle
                }}
                onTransitionEnd={(e) => {
                    if (e.target === sheetRef.current && animationState === 'closing') {
                        cleanupAndUnmount();
                    }
                }}
            >
                {/* Tirador de arrastre táctil (36x4px, centrado) */}
                {dragHandle && (
                    <div
                        className="w-full pt-2 pb-0 flex justify-center items-center cursor-grab active:cursor-grabbing select-none"
                        style={{ touchAction: 'none' }}
                        onPointerDown={handlePointerDown}
                    >
                        <div className="kl-sheet-handle" />
                    </div>
                )}

                {/* Cabecera de la hoja (sh-h: padding 8px 8px 10px 14px, b:16px, close:40x40) */}
                {(title || showCloseButton) && (
                    <div
                        className="px-3.5 py-2 border-b border-slate-100 dark:border-slate-800/80 flex items-center justify-between shrink-0"
                        style={{ touchAction: 'none' }}
                        onPointerDown={handlePointerDown}
                    >
                        <div className="flex-1 min-w-0 pr-2">
                            {title && (
                                <h3
                                    id={`${sheetId}-title`}
                                    className="text-[16px] font-bold text-slate-900 dark:text-white leading-tight truncate m-0"
                                >
                                    {title}
                                </h3>
                            )}
                            {subtitle && (
                                <div className="text-[12px] text-slate-500 dark:text-slate-400 font-medium truncate mt-0.5">
                                    {subtitle}
                                </div>
                            )}
                        </div>

                        {showCloseButton && (
                            <button
                                type="button"
                                data-drawer-close="true"
                                data-kl-modal-close="true"
                                onClick={onClose}
                                aria-label="Cerrar ventana"
                                className="w-10 h-10 rounded-full text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors active:scale-95 shrink-0"
                            >
                                <span className="material-symbols-outlined text-[22px]">close</span>
                            </button>
                        )}
                    </div>
                )}

                {/* Cuerpo con scroll propio */}
                <div
                    ref={bodyRef}
                    data-kl-sheet-body="true"
                    className={`flex-1 overflow-y-auto overscroll-contain ${contentClassName ? contentClassName : 'p-3.5'}`}
                    style={{ 
                        WebkitOverflowScrolling: 'touch',
                        paddingBottom: footer ? undefined : 'calc(14px + var(--kl-safe-bottom, env(safe-area-inset-bottom, 0px)))'
                    }}
                >
                    {children}
                </div>

                {/* Pie fijo (.foot: padding 10px 12px 14px + safe area bottom) */}
                {footer && (
                    <div
                        className="px-3 pt-2.5 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-[#1a2634] shrink-0"
                        style={{
                            paddingBottom: 'calc(14px + var(--kl-safe-bottom, env(safe-area-inset-bottom, 0px)))'
                        }}
                    >
                        {footer}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default KlSheet;
